import React, { useState, useEffect } from 'react'
import { 
  WrenchScrewdriverIcon, 
  PlusIcon, 
  CalendarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  TruckIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline'

interface Mantenimiento {
  id: string
  vehiculo: string
  placa: string
  tipo: 'preventivo' | 'correctivo' | 'emergencia'
  descripcion: string
  fechaProgramada: string
  fechaRealizada?: string
  estado: 'programado' | 'en_proceso' | 'completado' | 'vencido'
  costo?: number
  taller?: string
  proximoMantenimiento?: string
  kilometraje: number
}

const PaginaMantenimiento: React.FC = () => {
  const [mantenimientos, setMantenimientos] = useState<Mantenimiento[]>([])
  const [busqueda, setBusqueda] = useState('')
  const [filtroEstado, setFiltroEstado] = useState<string>('todos')
  const [filtroTipo, setFiltroTipo] = useState<string>('todos')

  useEffect(() => {
    // Datos de ejemplo
    const mantenimientosEjemplo: Mantenimiento[] = [
      {
        id: '1',
        vehiculo: 'Camión Mercedes-Benz',
        placa: 'ABC-123',
        tipo: 'preventivo',
        descripcion: 'Cambio de aceite y filtros',
        fechaProgramada: '2024-01-20',
        fechaRealizada: '2024-01-20',
        estado: 'completado',
        costo: 250000,
        taller: 'Taller Central',
        proximoMantenimiento: '2024-04-20',
        kilometraje: 45000
      },
      {
        id: '2',
        vehiculo: 'Camión Volvo',
        placa: 'DEF-456',
        tipo: 'correctivo',
        descripcion: 'Reparación de frenos',
        fechaProgramada: '2024-01-25',
        estado: 'en_proceso',
        taller: 'Taller Norte',
        kilometraje: 52000
      },
      {
        id: '3',
        vehiculo: 'Camión Scania',
        placa: 'GHI-789',
        tipo: 'preventivo',
        descripcion: 'Revisión general 10,000 km',
        fechaProgramada: '2024-01-15',
        estado: 'vencido',
        kilometraje: 38000
      },
      {
        id: '4',
        vehiculo: 'Camión Freightliner',
        placa: 'JKL-012',
        tipo: 'emergencia',
        descripcion: 'Falla en motor',
        fechaProgramada: '2024-01-30',
        estado: 'programado',
        kilometraje: 67000
      }
    ]
    setMantenimientos(mantenimientosEjemplo)
  }, [])

  const mantenimientosFiltrados = mantenimientos.filter(mantenimiento => {
    const cumpleBusqueda = mantenimiento.vehiculo.toLowerCase().includes(busqueda.toLowerCase()) ||
                          mantenimiento.placa.toLowerCase().includes(busqueda.toLowerCase()) ||
                          mantenimiento.descripcion.toLowerCase().includes(busqueda.toLowerCase())
    const cumpleFiltroEstado = filtroEstado === 'todos' || mantenimiento.estado === filtroEstado
    const cumpleFiltroTipo = filtroTipo === 'todos' || mantenimiento.tipo === filtroTipo
    return cumpleBusqueda && cumpleFiltroEstado && cumpleFiltroTipo
  })

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'completado':
        return 'text-green-600 bg-green-100'
      case 'en_proceso':
        return 'text-blue-600 bg-blue-100'
      case 'programado':
        return 'text-yellow-600 bg-yellow-100'
      case 'vencido':
        return 'text-red-600 bg-red-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const getTipoColor = (tipo: string) => {
    switch (tipo) {
      case 'preventivo':
        return 'text-green-600 bg-green-100'
      case 'correctivo':
        return 'text-yellow-600 bg-yellow-100'
      case 'emergencia':
        return 'text-red-600 bg-red-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const getEstadoIcon = (estado: string) => {
    switch (estado) {
      case 'completado':
        return <CheckCircleIcon className="h-4 w-4" />
      case 'en_proceso':
        return <ClockIcon className="h-4 w-4" />
      case 'programado':
        return <CalendarIcon className="h-4 w-4" />
      case 'vencido':
        return <ExclamationTriangleIcon className="h-4 w-4" />
      default:
        return <ClockIcon className="h-4 w-4" />
    }
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Mantenimiento de Flota</h1>
            <p className="text-gray-600 dark:text-gray-400">Gestiona el mantenimiento preventivo y correctivo de los vehículos</p>
          </div>
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
            <PlusIcon className="h-5 w-5" />
            <span>Programar Mantenimiento</span>
          </button>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <WrenchScrewdriverIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Mantenimientos</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{mantenimientos.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
              <CalendarIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Programados</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {mantenimientos.filter(m => m.estado === 'programado').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Completados</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {mantenimientos.filter(m => m.estado === 'completado').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Vencidos</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {mantenimientos.filter(m => m.estado === 'vencido').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros y búsqueda */}
      <div className="mb-6 flex flex-wrap gap-4">
        <div className="flex-1 min-w-64">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por vehículo, placa o descripción..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="pl-10 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
        </div>

        <div>
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="todos">Todos los estados</option>
            <option value="programado">Programado</option>
            <option value="en_proceso">En proceso</option>
            <option value="completado">Completado</option>
            <option value="vencido">Vencido</option>
          </select>
        </div>

        <div>
          <select
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
            className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="todos">Todos los tipos</option>
            <option value="preventivo">Preventivo</option>
            <option value="correctivo">Correctivo</option>
            <option value="emergencia">Emergencia</option>
          </select>
        </div>
      </div>

      {/* Lista de mantenimientos */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Vehículo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Descripción
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Fecha Programada
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Costo
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {mantenimientosFiltrados.map((mantenimiento) => (
                <tr key={mantenimiento.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <TruckIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {mantenimiento.vehiculo}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {mantenimiento.placa}
                        </div>
                        <div className="text-xs text-gray-400 dark:text-gray-500">
                          {mantenimiento.kilometraje.toLocaleString()} km
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTipoColor(mantenimiento.tipo)}`}>
                      {mantenimiento.tipo.charAt(0).toUpperCase() + mantenimiento.tipo.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {mantenimiento.descripcion}
                    </div>
                    {mantenimiento.taller && (
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        Taller: {mantenimiento.taller}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {new Date(mantenimiento.fechaProgramada).toLocaleDateString('es-ES')}
                    </div>
                    {mantenimiento.fechaRealizada && (
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        Realizado: {new Date(mantenimiento.fechaRealizada).toLocaleDateString('es-ES')}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getEstadoColor(mantenimiento.estado)}`}>
                      {getEstadoIcon(mantenimiento.estado)}
                      <span className="ml-1 capitalize">{mantenimiento.estado.replace('_', ' ')}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {mantenimiento.costo ? `$${mantenimiento.costo.toLocaleString()}` : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {mantenimientosFiltrados.length === 0 && (
          <div className="text-center py-8">
            <WrenchScrewdriverIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No hay mantenimientos</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              No se encontraron mantenimientos con los criterios de búsqueda.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default PaginaMantenimiento